import { format, formatDistanceToNow } from 'date-fns';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PageHeader } from '@/components/common/page-header';
import { SectionCard } from '@/components/common/section-card';
import { ActionMenu } from '@/components/fleet/action-menu';
import { AssociationEditor } from '@/components/fleet/association-editor';
import { AuditLog } from '@/components/fleet/audit-log';
import { CapabilityChip } from '@/components/fleet/capability-chip';
import { ConnectionModeEditor } from '@/components/fleet/connection-mode-editor';
import { DegradedIngressBanner } from '@/components/fleet/degraded-ingress-banner';
import { EventTimeline } from '@/components/fleet/event-timeline';
import { ExitIpEditor } from '@/components/fleet/exit-ip-editor';
import { LastSeen } from '@/components/fleet/last-seen';
import { DnsHostnameEditor } from '@/components/fleet/dns-hostname-editor';
import { NotesEditor } from '@/components/fleet/notes-editor';
import { ProtocolChip } from '@/components/fleet/protocol-chip';
import { ServerPlaybooksCard } from '@/components/fleet/server-playbooks-card';
import { ProviderBadge } from '@/components/fleet/provider-badge';
import { RegionBadge } from '@/components/fleet/region-badge';
import { ServerActionPanels } from '@/components/fleet/server-action-panels';
import { ServerLink } from '@/components/fleet/server-link';
import { ErrorState } from '@/components/fleet/states';
import { StatusBadge } from '@/components/fleet/status-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useFleetAction } from '@/hooks/use-fleet-action';
import { useFleetServer } from '@/hooks/use-fleet-server';
import { useLifecycleAction } from '@/hooks/use-lifecycle-action';
import type { ServerDetail } from '@/types/fleet';

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right">{children}</span>
    </div>
  );
}

function CopyableIp({ ip }: { ip: string }): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(ip);
      setCopied(true);
      toast.success(`Copied ${ip}`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="inline-flex items-center gap-1 font-mono hover:text-primary"
    >
      {ip}
      {copied ? <Check className="h-3 w-3" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
    </button>
  );
}

function IdentityCard({ server }: { server: ServerDetail }): JSX.Element {
  return (
    <SectionCard title="Identity" action={<ProviderBadge provider={server.provider} />}>
      <div className="divide-y divide-border">
        <div className="pb-2">
          <Field label="Public IP">
            <CopyableIp ip={server.public_ip} />
          </Field>
          <Field label="Private IP">
            <span className="font-mono">{server.private_ip ?? '—'}</span>
          </Field>
          <Field label="DNS hostname">
            {server.dns_hostname ? (
              <code className="rounded bg-secondary px-1 py-0.5 font-mono text-xs">
                {server.dns_hostname}
              </code>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
          <Field label="Region">
            <RegionBadge region={server.region} />
          </Field>
          <Field label="Role">
            <span className="capitalize">{server.role.replace('_', ' ')}</span>
          </Field>
        </div>
        <div className="py-2">
          <Field label="VM type">
            <span className="font-mono">{server.vm_type ?? '—'}</span>
          </Field>
          <Field label="CPU">
            <span className="font-mono">{server.cpu_cores ? `${server.cpu_cores} cores` : '—'}</span>
          </Field>
          <Field label="Memory">
            <span className="font-mono">{server.memory_gb ? `${server.memory_gb} GB` : '—'}</span>
          </Field>
          <Field label="Disk">
            <span className="font-mono">{server.disk_gb ? `${server.disk_gb} GB` : '—'}</span>
          </Field>
          <Field label="Bandwidth">
            <span className="font-mono">{server.bandwidth_tb ? `${server.bandwidth_tb} TB` : '—'}</span>
          </Field>
        </div>
        <div className="py-2">
          <Field label="Cloudflare tunnel">
            {server.cloudflare_tunnel_id ? (
              // No CF account id is available client-side, so a dashboard
              // deep-link would be broken — show the tunnel id as plain text.
              <span className="font-mono">{server.cloudflare_tunnel_id}</span>
            ) : (
              '—'
            )}
          </Field>
          <Field label="Commissioned">
            {server.commissioned_at ? (
              <span
                className="font-mono"
                title={format(new Date(server.commissioned_at), 'yyyy-MM-dd HH:mm')}
              >
                {formatDistanceToNow(new Date(server.commissioned_at), { addSuffix: true })}
              </span>
            ) : (
              '—'
            )}
          </Field>
          <Field label="Last seen">
            <LastSeen timestamp={server.last_seen_at} />
          </Field>
        </div>
        <div className="pt-2">
          <DnsHostnameEditor serverName={server.name} dnsHostname={server.dns_hostname} />
        </div>
        <div className="pt-2">
          <NotesEditor serverName={server.name} notes={server.notes} />
        </div>
      </div>
    </SectionCard>
  );
}

function OperationalCard({ server }: { server: ServerDetail }): JSX.Element {
  const imageVersions = server.current_image_versions
    ? Object.entries(server.current_image_versions)
    : [];

  return (
    <SectionCard title="Operational">
      <div className="space-y-4">
        <section>
          <h3 className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Protocols
          </h3>
          {server.protocols.length === 0 ? (
            <p className="text-sm text-muted-foreground">None enabled.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {server.protocols.map((p) => (
                <li key={p.protocol} className="flex items-center justify-between">
                  <ProtocolChip protocol={p.protocol} enabled={p.enabled} />
                  <span className="font-mono text-xs text-muted-foreground">
                    {p.port ?? '—'} · {p.enabled ? 'enabled' : 'disabled'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3 className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Capabilities
          </h3>
          {server.capabilities.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {server.capabilities.map((c) => (
                <CapabilityChip key={c} capability={c} />
              ))}
            </div>
          )}
        </section>

        <section>
          <h3 className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Image versions
          </h3>
          {imageVersions.length === 0 ? (
            <p className="text-sm text-muted-foreground">—</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {imageVersions.map(([proto, version]) => (
                  <tr key={proto}>
                    <td className="py-0.5 font-mono capitalize text-muted-foreground">{proto}</td>
                    <td className="py-0.5 text-right font-mono">{version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section>
          <h3 className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Upstream
          </h3>
          {server.associations.upstream.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {server.associations.upstream.map((a) => (
                <li key={a.name} className="flex items-center justify-between">
                  <ServerLink name={a.name} />
                  <span className="font-mono text-xs text-muted-foreground">{a.tunnel_proto}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3 className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Downstream
          </h3>
          {server.associations.downstream.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {server.associations.downstream.map((a) => (
                <li key={a.name} className="flex items-center justify-between">
                  <ServerLink name={a.name} />
                  <span className="font-mono text-xs text-muted-foreground">{a.tunnel_proto}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </SectionCard>
  );
}

/** The loaded-server view — split out so the action hooks run unconditionally. */
function ServerDetailView({ server }: { server: ServerDetail }): JSX.Element {
  const action = useFleetAction(server);
  const lifecycle = useLifecycleAction(server);

  // Pending overlay — only for state-changing actions, never an optimistic
  // flip. A destructive lifecycle run takes precedence over a generic action.
  const lifecycleOverlay =
    lifecycle.activeRun?.showOverlay === true
      ? {
          label: `${lifecycle.activeRun.action.label} in progress (task #${lifecycle.activeRun.taskId})`,
        }
      : undefined;
  const actionOverlay =
    action.activeRun?.showOverlay === true
      ? { label: `${action.activeRun.action.label} in progress (task #${action.activeRun.taskId})` }
      : undefined;
  const pendingOverlay = lifecycleOverlay ?? actionOverlay;

  return (
    <>
      <PageHeader
        title={server.name}
        backTo="/fleet"
        backLabel="Back to fleet"
        actions={<StatusBadge status={server.status} pending={pendingOverlay} />}
      />

      <DegradedIngressBanner serverName={server.name} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <IdentityCard server={server} />
        <OperationalCard server={server} />
        <div className="space-y-4">
          <SectionCard title="Actions">
            <ActionMenu server={server} action={action} lifecycle={lifecycle} />
          </SectionCard>
          <SectionCard title="Privileged actions">
            <AuditLog events={server.events} />
          </SectionCard>
          <ExitIpEditor serverName={server.name} />
          <ConnectionModeEditor server={server} />
          <AssociationEditor server={server} />
          <ServerPlaybooksCard server={server} />
          <SectionCard title="History">
            <EventTimeline serverName={server.name} />
          </SectionCard>
        </div>
      </div>

      <ServerActionPanels serverName={server.name} action={action} lifecycle={lifecycle} />
    </>
  );
}

export function FleetDetailPage(): JSX.Element {
  const { name } = useParams<{ name: string }>();
  const { data: server, isLoading, isError, error, refetch } = useFleetServer(name);

  return (
    <div className="space-y-6">
      {!server && (
        <PageHeader title={name ?? 'Server'} backTo="/fleet" backLabel="Back to fleet" />
      )}

      {isLoading && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-96 w-full rounded-md" />
          ))}
        </div>
      )}

      {isError && (
        <ErrorState
          title={`Couldn't load ${name ?? 'server'}.`}
          error={error}
          onRetry={() => void refetch()}
        />
      )}

      {server && <ServerDetailView server={server} />}
    </div>
  );
}
